/** Inventario: stock por producto × almacén (tríada), kardex, conteos y despiece de frágiles. Las piezas de vidrio están en Roturas y detalles. */
import { diaLegible, fechaCaracas, formatearCantidad, LIMITE_KARDEX, Money, TIPO_MOVIMIENTO_ETIQUETA, type AlmacenDTO, type StockFilaDTO, type SucursalDTO } from '@mg/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, ClipboardList, Scissors, FileSpreadsheet } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { useTemaAyuda } from '@/ayuda/contexto.tsx';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { PaginadorBusqueda, usePaginaBusqueda } from '@/componentes/PaginadorBusqueda.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Dinero } from '@/componentes/Dinero.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select, Textarea } from '@/componentes/ui/input.tsx';
import { Campo, Filtro } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { ImportarExcel } from '@/componentes/ImportarExcel.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { ErrorApi, get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { claves } from '@/lib/query.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { useApartado, useConfirmarConteo, useConteo, useConteos, useCrearConteo, useDespiece, useKardex, useRegistrarFisico, useStock } from './api.ts';
import { normalizarDecimal, parseMonto } from '@/lib/montos.ts';

const cant = (v: string | null) => (v === null ? '—' : formatearCantidad(Money.parse(v)));
type SucursalMia = SucursalDTO & { almacenes: AlmacenDTO[] };
const TEMA_PESTANA: Record<'stock' | 'conteos', string> = { stock: 'inventario.stock', conteos: 'inventario.conteos' };
/** Filas por página en la tabla de stock y en el selector de cajas del despiece. */
const LIMITE_STOCK = 100;
const LIMITE_DESPIEZABLES = 100;

export function Inventario() {
  const { puede, usuario } = useAuth();
  // La dueña (aun operando una tienda) y quien no tiene sucursal (consola global, contador) eligen la tienda: otra o todas
  const conSucursal = !!usuario?.sucursal_nombre;
  const veOtras = usuario?.rol === 'superadmin' || !conSucursal;
  const [tab, setTab] = useState<'stock' | 'conteos'>('stock');
  useTemaAyuda(TEMA_PESTANA[tab]);
  const [q, setQ] = useState('');
  const [almacenId, setAlmacenId] = useState('');
  const [soloStock, setSoloStock] = useState(true);
  const [soloApartado, setSoloApartado] = useState(false);
  // '' = la de la sesión (o todas, en la consola); un id; o 'todas'
  const [tienda, setTienda] = useState('');
  const [apartadoDe, setApartadoDe] = useState<StockFilaDTO | null>(null);
  const sucursales = useQuery({ queryKey: ['sucursales', 'todas'], queryFn: () => get<SucursalDTO[]>('/sucursales', { todas: '1' }), enabled: veOtras });
  const [kardexDe, setKardexDe] = useState<{ id: string; nombre: string } | null>(null);
  const [despieceAbierto, setDespieceAbierto] = useState(false);
  const [existenciasAbierto, setExistenciasAbierto] = useState(false);
  const qc = useQueryClient();
  const mia = useQuery({ queryKey: claves.sucursalMia, queryFn: () => get<SucursalMia | null>('/sucursales/mia') });
  // De 100 en 100 con paginador al pie (pedido de la dueña 2026-10-20: el catálogo real pasa de 500 productos)
  const [paginaStock, setPaginaStock] = usePaginaBusqueda(`${q}|${almacenId}|${soloStock}|${soloApartado}|${tienda}`);
  const stock = useStock({ q: q || undefined, almacen_id: almacenId || undefined, sucursal: tienda || undefined, solo_con_stock: soloStock, solo_apartado: soloApartado, pagina: paginaStock, limite: LIMITE_STOCK });
  const todasLasTiendas = stock.data ? stock.data.sucursal_id === null : !conSucursal;
  // Almacenes del filtro: los de la tienda del listado (o de todas, con su tienda delante)
  const almacenesFiltro = stock.data?.almacenes ?? (mia.data?.almacenes ?? []).map((a) => ({ id: a.id, nombre: a.nombre, tipo: a.tipo, sucursal_id: a.sucursal_id, sucursal_nombre: mia.data?.nombre ?? '' }));
  const puedeConteos = puede('inventario.conteos');

  return (
    <>
      <TituloPagina
        titulo="Inventario"
        subtitulo="Por almacén de cada tienda: lo que hay (existencia), lo vendido que falta por entregar (apartado) y lo que se puede vender (disponible = existencia − apartado)."
        acciones={
          <>
            {conSucursal && puede('inventario.ajustes', 'propone') ? <Button variant="outline" onClick={() => setExistenciasAbierto(true)} data-testid="btn-existencia-inicial"><FileSpreadsheet /> Existencia inicial (Excel)</Button> : null}
            {conSucursal && puedeConteos ? <Button variant="outline" onClick={() => setDespieceAbierto(true)} data-testid="btn-despiece"><Scissors /> Despiece de cajas</Button> : null}
          </>
        }
      />
      <ImportarExcel que="existencias" tema="inventario.existencia-inicial" abierto={existenciasAbierto} cerrar={() => setExistenciasAbierto(false)} almacenes={mia.data?.almacenes ?? []} alCargar={() => void qc.invalidateQueries()} />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="inline-flex overflow-hidden rounded-lg border">
          <button className={`px-4 py-1.5 text-sm font-medium ${tab === 'stock' ? 'bg-mg-verde text-white' : 'bg-card'}`} onClick={() => setTab('stock')}>Stock</button>
          {conSucursal && puedeConteos ? <button className={`px-4 py-1.5 text-sm font-medium ${tab === 'conteos' ? 'bg-mg-verde text-white' : 'bg-card'}`} onClick={() => setTab('conteos')} data-testid="tab-conteos">Conteos</button> : null}
        </div>
        {tab === 'stock' ? (
          <>
            <Input placeholder="Buscar producto…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="inv-buscar" />
            {veOtras ? (
              <Filtro etiqueta="Tienda">
                <Select value={tienda} onChange={(e) => { setTienda(e.target.value); setAlmacenId(''); }} className="w-52" aria-label="Tienda" data-testid="inv-tienda">
                  <option value="">{conSucursal ? `${usuario?.sucursal_nombre} (la que operas)` : 'Todas las tiendas'}</option>
                  {(sucursales.data ?? []).filter((s) => s.activa && s.nombre !== usuario?.sucursal_nombre).map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  {conSucursal ? <option value="todas">Todas las tiendas</option> : null}
                </Select>
              </Filtro>
            ) : null}
            <Filtro etiqueta="Almacén">
              <Select value={almacenId} onChange={(e) => setAlmacenId(e.target.value)} className="w-56" aria-label="Almacén" data-testid="inv-almacen">
                <option value="">Todos los almacenes</option>
                {almacenesFiltro.map((a) => <option key={a.id} value={a.id}>{todasLasTiendas ? `${a.sucursal_nombre} · ${a.nombre}` : a.nombre}</option>)}
              </Select>
            </Filtro>
            <label className="flex h-10 items-center gap-1.5 text-sm"><input type="checkbox" checked={soloStock} onChange={(e) => setSoloStock(e.target.checked)} data-testid="inv-solo-stock" /> Solo con existencia o apartado</label>
            <label className="flex h-10 items-center gap-1.5 text-sm"><input type="checkbox" checked={soloApartado} onChange={(e) => setSoloApartado(e.target.checked)} data-testid="inv-solo-apartado" /> Solo con apartado</label>
          </>
        ) : null}
      </div>
      {tab === 'stock' ? (
        <>
          <p className="mb-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground" data-testid="inv-leyenda">
            <span><b className="text-foreground">Existencia</b>: lo que hay en el almacén.</span>
            <span><b className="text-amber-800">Apartado (por entregar)</b>: vendido con factura o nota que falta por entregar, o apartado para un retiro en esta tienda. Pulsa el número para ver a quién.</span>
            <span><b className="text-mg-verde-oscuro">Disponible</b>: lo que se puede vender (existencia − apartado).</span>
          </p>
          <EstadoQuery cargando={stock.isLoading} error={stock.error} datos={stock.data} reintentar={() => void stock.refetch()}>
            {(d) =>
              d.datos.length === 0 ? (
                <Vacio icono={<Boxes className="h-6 w-6" />} titulo="Sin existencias" descripcion="Prueba quitando el filtro o busca otro producto." />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead>Tienda · almacén</TableHead>
                      <TableHead className="text-right">Existencia</TableHead>
                      <TableHead className="text-right" title="Vendido con factura o nota que falta por entregar">Apartado (por entregar)</TableHead>
                      <TableHead className="text-right">Disponible</TableHead>
                      <TableHead className="text-right">Mínimo</TableHead>
                      {d.datos[0]?.costo_real !== null ? <TableHead className="text-right">Valor (costo)</TableHead> : null}
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.datos.map((s, i) => {
                      const siguiente = d.datos[i + 1];
                      const ultimoDelProducto = !siguiente || siguiente.producto_id !== s.producto_id;
                      // Con varias tiendas en el listado: subtotal de cada tienda del producto (si tiene más de un almacén allí)
                      const ultimoDeLaTienda = ultimoDelProducto || siguiente.sucursal_id !== s.sucursal_id;
                      const conSubtotal = ultimoDeLaTienda && s.total.almacenes > s.total_tienda.almacenes && s.total_tienda.almacenes > 1;
                      const apartado = Money.parse(s.pendiente);
                      const disponible = Money.parse(s.disponible);
                      const bajoMinimo = s.minimo !== null && disponible.lt(Money.parse(s.minimo));
                      // El kardex es de la tienda que se opera (o de todas, en la consola): no se ofrece en filas de otra tienda
                      const conKardex = !conSucursal || s.sucursal_id === usuario?.sucursal_id;
                      return (
                        <Fragment key={`${s.producto_id}-${s.almacen_id}`}>
                          <TableRow data-testid={`stock-${s.codigo}-${s.almacen_nombre}`}>
                            <TableCell><p className="font-medium">{s.nombre}</p><p className="text-[11px] text-muted-foreground">{s.codigo} · {s.unidad}{s.fragil ? ' · frágil' : ''}</p></TableCell>
                            <TableCell><p>{s.almacen_nombre}</p><p className="text-[11px] text-muted-foreground">{s.sucursal_nombre} · {s.almacen_tipo === 'tienda' ? 'tienda' : 'depósito'}</p></TableCell>
                            <TableCell className="text-right num">{cant(s.existencia)}</TableCell>
                            <TableCell className="text-right num">
                              {apartado.esPositivo() ? (
                                <button type="button" className="rounded-md bg-amber-50 px-2 py-0.5 font-semibold text-amber-800 underline-offset-2 hover:underline" title="Ver a quién está apartado" onClick={() => setApartadoDe(s)} data-testid={`apartado-${s.codigo}-${s.almacen_nombre}`}>{cant(s.pendiente)}</button>
                              ) : <span className="text-muted-foreground">{cant(s.pendiente)}</span>}
                            </TableCell>
                            <TableCell className="text-right num">
                              <span className={`font-semibold ${disponible.esNegativo() ? 'text-mg-rojo' : 'text-mg-verde-oscuro'}`} data-testid={`disponible-${s.codigo}-${s.almacen_nombre}`}>{cant(s.disponible)}</span>
                              {bajoMinimo ? <Badge variant="pendiente" className="ml-1" title={`Disponible por debajo del mínimo (${cant(s.minimo)})`} data-testid={`bajo-minimo-${s.codigo}-${s.almacen_nombre}`}>bajo mínimo</Badge> : null}
                            </TableCell>
                            <TableCell className="text-right num">{s.minimo ? cant(s.minimo) : '—'}</TableCell>
                            {s.costo_real !== null ? <TableCell className="text-right num">$ {Money.parse(s.existencia).mul(s.costo_real).toDisplay()}</TableCell> : null}
                            <TableCell className="text-right">{conKardex ? <Button size="sm" variant="ghost" onClick={() => setKardexDe({ id: s.producto_id, nombre: s.nombre })} data-testid={`kardex-${s.codigo}`}>Kardex</Button> : null}</TableCell>
                          </TableRow>
                          {conSubtotal ? (
                            <TableRow className="bg-muted/20 text-[13px]" data-testid={`subtotal-${s.codigo}-${s.sucursal_nombre}`}>
                              <TableCell colSpan={2} className="pl-6">Subtotal {s.sucursal_nombre}<span className="block text-[11px] text-muted-foreground">en {s.total_tienda.almacenes} almacenes de esta tienda</span></TableCell>
                              <TableCell className="text-right num">{cant(s.total_tienda.existencia)}</TableCell>
                              <TableCell className={`text-right num ${Money.parse(s.total_tienda.pendiente).esPositivo() ? 'text-amber-800' : 'text-muted-foreground'}`}>{cant(s.total_tienda.pendiente)}</TableCell>
                              <TableCell className={`text-right num ${Money.parse(s.total_tienda.disponible).esNegativo() ? 'text-mg-rojo' : 'text-mg-verde-oscuro'}`}>{cant(s.total_tienda.disponible)}</TableCell>
                              <TableCell />
                              {s.costo_real !== null ? <TableCell className="text-right num">$ {Money.parse(s.total_tienda.existencia).mul(s.costo_real).toDisplay()}</TableCell> : null}
                              <TableCell />
                            </TableRow>
                          ) : null}
                          {ultimoDelProducto && s.total.almacenes > 1 ? (
                            <TableRow className="bg-muted/40 font-semibold" data-testid={`total-${s.codigo}`}>
                              <TableCell colSpan={2}>Total · {s.nombre}<span className="block text-[11px] font-normal text-muted-foreground">en {s.total.almacenes} almacenes{todasLasTiendas ? ' de las tiendas' : ''}</span></TableCell>
                              <TableCell className="text-right num">{cant(s.total.existencia)}</TableCell>
                              <TableCell className={`text-right num ${Money.parse(s.total.pendiente).esPositivo() ? 'text-amber-800' : 'text-muted-foreground'}`}>{cant(s.total.pendiente)}</TableCell>
                              <TableCell className={`text-right num ${Money.parse(s.total.disponible).esNegativo() ? 'text-mg-rojo' : 'text-mg-verde-oscuro'}`}>{cant(s.total.disponible)}</TableCell>
                              <TableCell />
                              {s.costo_real !== null ? <TableCell className="text-right num">$ {Money.parse(s.total.existencia).mul(s.costo_real).toDisplay()}</TableCell> : null}
                              <TableCell />
                            </TableRow>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )
            }
          </EstadoQuery>
          {stock.data && !stock.error ? (
            <PaginadorBusqueda
              pagina={paginaStock}
              limite={LIMITE_STOCK}
              total={stock.data.total}
              onPagina={(n) => {
                setPaginaStock(n);
                window.scrollTo({ top: 0 });
              }}
              testid="inv-stock"
              className="mt-2 rounded-lg border"
            />
          ) : null}
        </>
      ) : (
        <Conteos almacenes={mia.data?.almacenes ?? []} />
      )}
      <KardexDialog producto={kardexDe} cerrar={() => setKardexDe(null)} />
      <ApartadoDialog fila={apartadoDe} cerrar={() => setApartadoDe(null)} />
      <DespieceDialog abierto={despieceAbierto} cerrar={() => setDespieceAbierto(false)} almacenes={mia.data?.almacenes ?? []} />
    </>
  );
}

/** A quién está apartado un producto en un almacén: cada venta por entregar y cada orden de retiro (pedido del 2026-11-01). */
function ApartadoDialog({ fila, cerrar }: { fila: StockFilaDTO | null; cerrar: () => void }) {
  const a = useApartado(fila ? { producto_id: fila.producto_id, almacen_id: fila.almacen_id } : null);
  return (
    <Dialog open={!!fila} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-3xl" data-testid="dialogo-apartado">
        <DialogHeader>
          <DialogTitle>Apartado · {fila?.nombre}</DialogTitle>
          <DialogDescription>{fila?.almacen_nombre} · {fila?.sucursal_nombre}. Lo vendido que falta por entregar en este almacén, y lo apartado aquí para un cliente que retira en esta tienda.</DialogDescription>
        </DialogHeader>
        <EstadoQuery cargando={a.isLoading} error={a.error} datos={a.data} reintentar={() => void a.refetch()}>
          {(d) => (
            <>
              <Table>
                <TableHeader><TableRow><TableHead>Documento</TableHead><TableHead>Cliente</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Apartado</TableHead><TableHead>Nota</TableHead></TableRow></TableHeader>
                <TableBody>
                  {d.renglones.map((r, i) => (
                    <TableRow key={`${r.documento_id}-${i}`} data-testid={`apartado-renglon-${r.numero_formateado}`}>
                      <TableCell className="whitespace-nowrap"><Link to={`/documentos/${r.documento_id}`} className="underline">{r.tipo} {r.numero_formateado}</Link>{r.sucursal_venta_nombre !== d.almacen.sucursal_nombre ? <span className="block text-[11px] text-muted-foreground">venta de {r.sucursal_venta_nombre}</span> : null}</TableCell>
                      <TableCell>{r.cliente_razon_social}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs">{diaLegible(fechaCaracas(r.emitido_en))}</TableCell>
                      <TableCell className="text-right num font-semibold">{cant(r.cantidad)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {r.origen === 'orden_retiro' && r.orden ? `Orden de retiro ${r.orden.numero_formateado}: el cliente lo retira aquí (vence el ${diaLegible(fechaCaracas(r.orden.vence_en))}).` : r.orden ? `Otras ${cant(r.orden.cantidad)} se retiran en ${r.orden.sucursal_retiro_nombre} (orden ${r.orden.numero_formateado}).` : 'Por entregar.'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-right text-sm" data-testid="apartado-total">Total apartado: <b className="num">{cant(d.apartado)} {d.producto.unidad}</b></p>
              {d.suma !== d.apartado ? <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-800" role="alert">Los documentos suman {cant(d.suma)} y el inventario dice {cant(d.apartado)}: avísale a soporte.</p> : null}
            </>
          )}
        </EstadoQuery>
      </DialogContent>
    </Dialog>
  );
}

function KardexDialog({ producto, cerrar }: { producto: { id: string; nombre: string } | null; cerrar: () => void }) {
  const k = useKardex(producto?.id ?? null);
  // La frase de la suma solo vale sin recorte; el valor en Bs de una compra lo trae la API solo a quien ve costos
  // (re-auditoría 2026-10-18, ronda 2, hallazgos 3-H12 y 4-H23)
  const recortado = !!k.data && k.data.length >= LIMITE_KARDEX;
  const conValor = !!k.data?.some((m) => m.valor_bs);
  return (
    <Dialog open={!!producto} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Kardex · {producto?.nombre}</DialogTitle>
          <DialogDescription>Cada movimiento con existencia y pendiente antes/después.{recortado ? <span className="block text-amber-800" data-testid="kardex-recorte">Se muestran los {LIMITE_KARDEX} movimientos más recientes: la suma de estos ya no da toda la existencia.</span> : <span data-testid="kardex-suma"> La suma reconstruye el stock.</span>}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <EstadoQuery cargando={k.isLoading} error={k.error} datos={k.data} vacio={(d) => d.length === 0} reintentar={() => void k.refetch()}>
            {(movs) => (
              <Table>
                <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Almacén</TableHead><TableHead>Tipo</TableHead><TableHead>Ref.</TableHead><TableHead className="text-right">Δ exist.</TableHead><TableHead className="text-right">Exist. después</TableHead><TableHead className="text-right">Pend. después</TableHead>{conValor ? <TableHead className="text-right">Valor Bs</TableHead> : null}<TableHead>Usuario</TableHead></TableRow></TableHeader>
                <TableBody>
                  {movs.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>{formatearFechaHora(m.ocurrido_en)}</TableCell>
                      <TableCell>{m.almacen_nombre}</TableCell>
                      <TableCell><Badge variant="neutro">{m.etiqueta ?? TIPO_MOVIMIENTO_ETIQUETA[m.tipo] ?? m.tipo}</Badge></TableCell>
                      <TableCell className="text-xs">{m.referencia_numero ?? m.referencia_tipo ?? '—'}{m.motivo ? ` · ${m.motivo}` : ''}</TableCell>
                      <TableCell className={`text-right num ${Money.parse(m.cantidad).esNegativo() ? 'text-mg-rojo' : Money.parse(m.cantidad).esPositivo() ? 'text-mg-verde' : ''}`}>{cant(m.cantidad)}</TableCell>
                      <TableCell className="text-right num">{cant(m.existencia_despues)}</TableCell>
                      <TableCell className="text-right num">{cant(m.pendiente_despues)}</TableCell>
                      {conValor ? <TableCell className="text-right">{m.valor_bs ? <Dinero valor={m.valor_bs} moneda="BS" modo="bs" /> : '—'}</TableCell> : null}
                      <TableCell>{m.usuario_nombre ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </EstadoQuery>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DespieceDialog({ abierto, cerrar, almacenes }: { abierto: boolean; cerrar: () => void; almacenes: AlmacenDTO[] }) {
  const [almacenId, setAlmacenId] = useState('');
  const [productoId, setProductoId] = useState('');
  const [cajas, setCajas] = useState('1');
  const [piezas, setPiezas] = useState('');
  const [just, setJust] = useState('');
  const [buscar, setBuscar] = useState('');
  // Solo las cajas que se despiezan (frágiles con su pieza suelta), con buscador: el catálogo real tiene cientos de productos
  // De 100 en 100 con paginador debajo de la lista (pedido de la dueña 2026-10-20)
  const [pagina, setPagina] = usePaginaBusqueda(buscar);
  const fragiles = useQuery({ queryKey: ['productos', 'despiezables', buscar, pagina], queryFn: () => get<{ datos: { id: string; codigo: string; nombre: string }[]; total: number }>('/productos', { q: buscar || undefined, despiezables: 'true', pagina, limite: LIMITE_DESPIEZABLES }), enabled: abierto, placeholderData: (p) => p });
  // La caja elegida sigue en la lista aunque se pase de página o se busque otra cosa
  const [elegida, setElegida] = useState<{ id: string; codigo: string; nombre: string } | null>(null);
  const dePagina = fragiles.data?.datos ?? [];
  const opcionesCaja = elegida && elegida.id === productoId && !dePagina.some((p) => p.id === elegida.id) ? [elegida, ...dePagina] : dePagina;
  const despiece = useDespiece();
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Despiece de cajas (frágiles)</DialogTitle><AyudaVentana tema="inventario.despiece" /></div>
          <DialogDescription>Las cajas rotas salen del inventario y entran sus piezas sueltas, que se venden normal. No salen más piezas de las que traen las cajas; lo que falta queda como merma, a su costo.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Almacén" htmlFor="dp-alm"><Select id="dp-alm" value={almacenId} onChange={(e) => setAlmacenId(e.target.value)}><option value="">—</option>{almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}</Select></Campo>
          <Campo etiqueta="Producto (caja)" htmlFor="dp-prod" ayuda={fragiles.data && fragiles.data.datos.length === 0 ? 'Ninguna caja con esa búsqueda tiene su pieza suelta en la ficha' : 'Solo las cajas frágiles con su pieza suelta'}>
            <div className="space-y-1">
              <Input placeholder="Buscar…" value={buscar} onChange={(e) => setBuscar(e.target.value)} className="h-8" data-testid="despiece-buscar" />
              <Select id="dp-prod" value={productoId} onChange={(e) => { setProductoId(e.target.value); setElegida(opcionesCaja.find((p) => p.id === e.target.value) ?? null); }} data-testid="despiece-producto"><option value="">—</option>{opcionesCaja.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}</Select>
              <PaginadorBusqueda pagina={pagina} limite={LIMITE_DESPIEZABLES} total={fragiles.data?.total ?? 0} onPagina={setPagina} testid="despiece-producto" className="rounded-lg border" />
            </div>
          </Campo>
          <Campo etiqueta="Cajas rotas" htmlFor="dp-cajas"><Input id="dp-cajas" inputMode="decimal" value={cajas} onChange={(e) => setCajas(e.target.value)} data-testid="despiece-cajas" /></Campo>
          <Campo etiqueta="Piezas recuperadas" htmlFor="dp-piezas"><Input id="dp-piezas" inputMode="decimal" value={piezas} onChange={(e) => setPiezas(e.target.value)} data-testid="despiece-piezas" /></Campo>
        </div>
        <Campo etiqueta="Justificación" htmlFor="dp-just"><Textarea id="dp-just" value={just} onChange={(e) => setJust(e.target.value)} rows={2} data-testid="despiece-just" /></Campo>
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>Cancelar</Button>
          <Button disabled={!almacenId || !productoId || !cajas || piezas === '' || just.trim().length < 5} cargando={despiece.isPending} onClick={() => despiece.mutate({ almacen_id: almacenId, producto_id: productoId, cajas, piezas, justificacion: just }, { onSuccess: (a) => { toast.success(a.estado === 'pendiente' ? `Despiece ${a.numero_formateado} registrado: la merma queda por aprobar` : `Despiece ${a.numero_formateado} registrado`, { description: a.estado === 'pendiente' ? 'La existencia cambia cuando lo apruebe quien autoriza ajustes.' : a.justificacion }); cerrar(); }, onError: (e) => toast.error('No se pudo despiezar', { description: mensajeDeError(e) }) })} data-testid="despiece-confirmar">
            Registrar despiece
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Conteos({ almacenes }: { almacenes: AlmacenDTO[] }) {
  const lista = useConteos();
  const crear = useCrearConteo();
  const [almacenId, setAlmacenId] = useState('');
  const [abierto, setAbierto] = useState<string | null>(null);
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2">
        <Filtro etiqueta="Almacén a contar">
          <Select value={almacenId} onChange={(e) => setAlmacenId(e.target.value)} className="w-56" aria-label="Almacén a contar" data-testid="conteo-almacen"><option value="">Almacén…</option>{almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}</Select>
        </Filtro>
        <Button disabled={!almacenId} cargando={crear.isPending} onClick={() => crear.mutate({ almacen_id: almacenId }, { onSuccess: (c) => { toast.success(`Conteo ${c.numero_formateado} creado`, { description: 'Guardó lo que el sistema tiene registrado en ese almacén: escribe ahora lo contado.' }); setAbierto(c.id); }, onError: (e) => toast.error(mensajeDeError(e)) })} data-testid="conteo-crear"><ClipboardList /> Nuevo conteo</Button>
      </div>
      <EstadoQuery cargando={lista.isLoading} error={lista.error} datos={lista.data} reintentar={() => void lista.refetch()}>
        {(d) =>
          d.datos.length === 0 ? (
            <Vacio titulo="Sin conteos" descripcion="Crea un conteo por almacén: toma una foto del sistema y compara con lo físico." />
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Conteo</TableHead><TableHead>Almacén</TableHead><TableHead>Estado</TableHead><TableHead>Creado</TableHead><TableHead>Ajuste</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {d.datos.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.numero_formateado}</TableCell>
                    <TableCell>{c.almacen_nombre}</TableCell>
                    <TableCell><Badge variant={c.estado === 'confirmado' ? 'ok' : 'pendiente'}>{c.estado}</Badge></TableCell>
                    <TableCell>{formatearFechaHora(c.creado_en)} · {c.creado_por_nombre}</TableCell>
                    <TableCell>{c.ajuste_numero ?? '—'}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setAbierto(c.id)}>{c.estado === 'borrador' ? 'Capturar físico' : 'Ver'}</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </EstadoQuery>
      <ConteoDialog id={abierto} cerrar={() => setAbierto(null)} />
    </div>
  );
}

function ConteoDialog({ id, cerrar }: { id: string | null; cerrar: () => void }) {
  const c = useConteo(id);
  const fisico = useRegistrarFisico(id ?? '');
  const confirmar = useConfirmarConteo(id ?? '');
  const [valores, setValores] = useState<Record<string, string>>({});
  const lineas = c.data?.lineas ?? [];

  /** Guarda solo lo tecleado (sin igualar al sistema lo que no se contó). */
  const guardarTecleados = async () => {
    const tecleadas = lineas
      .filter((l) => (valores[l.producto_id] ?? '').trim() !== '')
      .map((l) => ({ producto_id: l.producto_id, fisico: normalizarDecimal(valores[l.producto_id] ?? '') }));
    if (tecleadas.length > 0) await fisico.mutateAsync(tecleadas);
  };

  /** Confirmar = guardar lo tecleado y confirmar en un solo clic (probando en local se pulsaba confirmar sin guardar). */
  const alConfirmar = async (forzar: boolean): Promise<void> => {
    try {
      await guardarTecleados();
      const r = await confirmar.mutateAsync(forzar);
      toast.success(forzar ? 'Conteo confirmado (forzado)' : `Conteo confirmado${r.ajuste_numero ? ` · ajuste ${r.ajuste_numero} pendiente de aprobación` : ' · sin diferencias'}`);
      cerrar();
    } catch (e) {
      const m = mensajeDeError(e);
      if (!forzar && /forzar/.test(m) && confirm(`${m}\n\n¿Confirmar de todas formas?`)) return alConfirmar(true);
      if (e instanceof ErrorApi && e.codigo === 'conteo_incompleto') {
        toast.error(m, { description: 'Escribe la cantidad física de cada producto, o pulsa "Guardar físico" para igualar al sistema los que no contaste.' });
        return;
      }
      toast.error(m);
    }
  };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Conteo {c.data?.numero_formateado} · {c.data?.almacen_nombre}</DialogTitle><AyudaVentana tema="inventario.conteo" /></div>
          <DialogDescription>Lo que el sistema tenía al abrir el conteo frente a lo contado. Las diferencias generan un ajuste «Diferencia de conteo» que aprueba la dueña.{c.data && c.data.movimientos_desde_snapshot > 0 ? ` ⚠ ${c.data.movimientos_desde_snapshot} producto(s) ya no tienen la existencia que tenían al abrir el conteo (hubo movimientos desde entonces).` : ''}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Producto</TableHead><TableHead className="text-right">Sistema</TableHead><TableHead className="text-right">Físico</TableHead><TableHead className="text-right">Diferencia</TableHead></TableRow></TableHeader>
            <TableBody>
              {lineas.map((l) => (
                <TableRow key={l.producto_id}>
                  <TableCell>{l.producto_nombre} <span className="text-[11px] text-muted-foreground">{l.producto_codigo}</span></TableCell>
                  <TableCell className="text-right num">{cant(l.sistema)}</TableCell>
                  <TableCell className="text-right">{c.data?.estado === 'borrador' ? <Input inputMode="decimal" value={valores[l.producto_id] ?? l.fisico ?? ''} onChange={(e) => setValores((v) => ({ ...v, [l.producto_id]: e.target.value }))} className="h-8 w-24 text-right" data-testid={`fisico-${l.producto_codigo}`} /> : cant(l.fisico)}</TableCell>
                  {(() => {
                    // Diferencia en vivo con lo tecleado (antes de guardar); si no hay tecleado, la guardada
                    const tecleado = (valores[l.producto_id] ?? '').trim();
                    const dif = parseMonto(tecleado)?.sub(l.sistema) ?? (l.diferencia ? Money.parse(l.diferencia) : null);
                    return (
                      <TableCell className={`text-right num ${dif && !dif.esCero() ? 'font-semibold text-mg-ambar' : ''}`}>
                        {dif ? `${dif.esPositivo() ? '+' : ''}${cant(dif.toString())}` : '—'}
                      </TableCell>
                    );
                  })()}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {c.data?.estado === 'borrador' ? (
          <DialogFooter>
            <Button variant="outline" cargando={fisico.isPending} onClick={() => fisico.mutate(lineas.map((l) => ({ producto_id: l.producto_id, fisico: normalizarDecimal(valores[l.producto_id] ?? l.fisico ?? l.sistema) })), { onSuccess: () => toast.success('Físico guardado'), onError: (e) => toast.error(mensajeDeError(e)) })} data-testid="conteo-guardar-fisico">Guardar físico</Button>
            <Button cargando={fisico.isPending || confirmar.isPending} onClick={() => void alConfirmar(false)} data-testid="conteo-confirmar">Confirmar conteo</Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
